import { useCallback, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, ExternalLink, FileText, Loader2, Mail, Paperclip, Sparkles, UploadCloud } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  type CandidateRow,
  type DocumentEvent,
  type DocumentRow,
  type DocumentStatus,
  type UploadResult,
  documentDownloadUrl,
  getDocumentEvents,
  getIngestionStatus,
  getRecentIngestion,
  ingestEmail,
  ingestJd,
  listCandidates,
  uploadFiles,
} from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/upload')({
  component: UploadPage,
})

function fmtExp(months: number | null): string {
  if (months == null) return '—'
  return `${Math.floor(months / 12)}y ${months % 12}m`
}

const STAT_TILES = ['Queued', 'Running', 'Done', 'Candidates'] as const

function UploadPage() {
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<UploadResult[]>([])
  const [rows, setRows] = useState<CandidateRow[]>([])
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const status = useQuery({
    queryKey: ['ingestion-status'],
    queryFn: getIngestionStatus,
    refetchInterval: 1000,
  })

  const loadCandidates = useCallback(
    async (reset: boolean) => {
      setLoadingMore(true)
      try {
        const next = reset ? 0 : page + 1
        const res = await listCandidates({ sort: 'recent', page: next, pageSize: 10 })
        setRows((prev) => (reset ? res.candidates : [...prev, ...res.candidates]))
        setPage(next)
        setTotal(res.total)
      } finally {
        setLoadingMore(false)
      }
    },
    [page],
  )

  const onFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return
      setUploading(true)
      try {
        const res = await uploadFiles(Array.from(files))
        setResults(res.uploaded)
        const queued = res.uploaded.filter((r) => r.status === 'queued').length
        toast.success(`Uploaded ${files.length} file(s) — ${queued} queued for processing`)
        setTimeout(() => loadCandidates(true), 1500)
      } catch {
        toast.error('Upload failed — is the API running on :8000?')
      } finally {
        setUploading(false)
      }
    },
    [loadCandidates],
  )

  const q = status.data?.queue
  const tileValue: Record<(typeof STAT_TILES)[number], number | undefined> = {
    Queued: q?.queued,
    Running: q?.running,
    Done: q?.done,
    Candidates: status.data?.candidates,
  }

  return (
    <DashboardLayout title="Upload" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Upload & Ingestion"
        subtitle="Drop CVs or candidate sheets — watch them get parsed, redacted, deduped, and added live."
      />

      {/* Autonomous intake reflexes */}
      <IntakePanel />

      {/* Dropzone */}
      <Card className="mb-6">
        <CardContent className="pt-5 pb-5">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              onFiles(e.dataTransfer.files)
            }}
            className={`flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:bg-muted/50'
            }`}
          >
            {uploading ? (
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
            ) : (
              <UploadCloud className="mb-3 h-8 w-8 text-muted-foreground" />
            )}
            <p className="text-sm font-medium">
              {uploading ? 'Uploading…' : 'Drag CVs or a candidate sheet here, or click to browse'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">PDF · DOCX · CSV · XLSX — one file or thousands</p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.csv,.xlsx"
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />
          </button>

          {results.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {results.map((r, i) => (
                <div key={`${r.name}-${i}`} className="flex items-center gap-2 text-sm">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.rows != null && <span className="text-xs text-muted-foreground">{r.rows} rows</span>}
                  <Badge variant={r.status === 'queued' ? 'success' : r.status === 'duplicate' ? 'warning' : 'info'}>
                    {r.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live queue dashboard */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STAT_TILES.map((label) => (
          <Card key={label}>
            <CardContent className="py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">{tileValue[label] ?? '—'}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-file ingestion progress */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-medium">Recent uploads</h2>
            <p className="text-[11px] text-muted-foreground">Live status per file — auto-refreshes while processing.</p>
          </div>
          <RecentUploads />
        </CardContent>
      </Card>

      {/* Real ingested candidates */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="text-sm font-medium">Ingested candidates</h2>
            <Button size="sm" onClick={() => loadCandidates(true)}>
              Refresh
            </Button>
          </div>
          {rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No candidates loaded yet — upload a file, then Refresh.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((c) => (
                <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{c.name ?? '—'}</span>
                  <span className="hidden min-w-0 flex-1 truncate text-muted-foreground sm:block">{c.employer ?? '—'}</span>
                  <span className="w-16 shrink-0 text-right text-muted-foreground tabular-nums">
                    {fmtExp(c.totalMonthsExp)}
                  </span>
                  <span className="hidden w-28 shrink-0 truncate text-muted-foreground md:block">
                    {c.location ?? '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {rows.length > 0 && rows.length < total && (
            <div className="border-t border-border px-4 py-3 text-center">
              <Button size="sm" disabled={loadingMore} onClick={() => loadCandidates(false)}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

// ─── Per-file ingestion tracker ───────────────────────────────────────────────

const TERMINAL_STATUSES = new Set<DocumentStatus>(['processed', 'review', 'failed', 'quarantined', 'skipped'])

// Maps the raw pipeline status to a human-readable stage label and a step index (0-based).
// Steps: uploaded(0) → scanned(1) → extracting(2) → parsing(3) → ready/review(4)
// Terminal error states sit outside the main rail.
const STATUS_META: Record<
  DocumentStatus,
  { label: string; step: number; variant: 'success' | 'warning' | 'error' | 'info' | 'secondary' }
> = {
  stored:      { label: 'Uploaded',    step: 0, variant: 'secondary' },
  queued:      { label: 'Queued',      step: 0, variant: 'secondary' },
  scanned:     { label: 'Scanned',     step: 1, variant: 'info'      },
  extracting:  { label: 'Extracting',  step: 2, variant: 'info'      },
  parsing:     { label: 'Parsing',     step: 3, variant: 'info'      },
  processing:  { label: 'Processing',  step: 3, variant: 'info'      },
  review:      { label: 'Needs review',step: 4, variant: 'warning'   },
  processed:   { label: 'Ready',       step: 4, variant: 'success'   },
  failed:      { label: 'Failed',      step: -1, variant: 'error'    },
  quarantined: { label: 'Quarantined', step: -1, variant: 'error'    },
  skipped:     { label: 'Skipped',     step: -1, variant: 'secondary'},
}

const STAGE_LABELS = ['Uploaded', 'Scanned', 'Extracting', 'Parsing', 'Ready'] as const

function StageRail({ status }: { status: DocumentStatus }) {
  const meta = STATUS_META[status]
  if (meta.step === -1) return null // terminal error — shown via badge only
  return (
    <div className="mt-1.5 flex items-center gap-0.5">
      {STAGE_LABELS.map((label, i) => (
        <div key={label} className="flex items-center gap-0.5">
          <div
            className={cn(
              'h-1.5 w-6 rounded-full transition-colors',
              i < meta.step
                ? 'bg-primary/70'
                : i === meta.step
                  ? 'bg-primary'
                  : 'bg-border',
            )}
            title={label}
          />
        </div>
      ))}
      <span className="ml-1.5 text-[10px] text-muted-foreground">{meta.label}</span>
    </div>
  )
}

function fmtRelTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  return `${Math.floor(m / 60)}h ago`
}

function EventTimeline({ docId }: { docId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['doc-events', docId],
    queryFn: () => getDocumentEvents(docId),
    staleTime: 30_000,
  })

  if (isLoading)
    return <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading timeline…</div>
  if (isError)
    return <div className="mt-2 text-xs text-destructive">Could not load timeline.</div>

  const events: DocumentEvent[] = data?.events ?? []
  if (events.length === 0)
    return <div className="mt-2 text-xs text-muted-foreground">No events yet.</div>

  return (
    <ol className="mt-2 space-y-1 border-l border-border pl-3">
      {events.map((ev, i) => {
        const ms = (ev.detail as Record<string, unknown> | null)?.ms
        return (
          <li key={i} className="relative text-xs">
            <span className="absolute -left-[13px] top-1 h-1.5 w-1.5 rounded-full bg-border ring-2 ring-background" />
            <span className="font-medium text-foreground">
              {ev.from_status ? `${ev.from_status} → ` : ''}{ev.to_status}
            </span>
            {typeof ms === 'number' && (
              <span className="ml-1.5 text-muted-foreground">{ms}ms</span>
            )}
            <span className="ml-1.5 text-muted-foreground">{fmtRelTime(ev.created_at)}</span>
          </li>
        )
      })}
    </ol>
  )
}

function DocumentItem({ doc }: { doc: DocumentRow }) {
  const [expanded, setExpanded] = useState(false)
  const [downloadHref, setDownloadHref] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  const meta = STATUS_META[doc.status]
  const canDownload = doc.status === 'processed' || doc.status === 'review'

  const openCv = async () => {
    if (downloading) return
    if (downloadHref) { window.open(downloadHref, '_blank'); return }
    setDownloading(true)
    try {
      const url = await documentDownloadUrl(doc.id)
      setDownloadHref(url)
      window.open(url, '_blank')
    } catch {
      toast.error('Could not download the file.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={doc.file_name}>
              {doc.file_name}
            </span>
            <Badge variant={meta.variant}>{meta.label}</Badge>
          </div>

          <StageRail status={doc.status} />

          {(doc.status === 'failed' || doc.status === 'quarantined') && doc.error && (
            <p className="mt-1 text-xs text-destructive truncate" title={doc.error}>{doc.error}</p>
          )}

          <div className="mt-1.5 flex items-center gap-3">
            <span className="text-[11px] text-muted-foreground">{fmtRelTime(doc.created_at)}</span>
            {canDownload && (
              <button
                type="button"
                onClick={openCv}
                disabled={downloading}
                className="inline-flex items-center gap-1 text-[11px] text-info hover:underline disabled:opacity-50"
              >
                {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                View CV
              </button>
            )}
            {canDownload && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
              >
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Details
              </button>
            )}
          </div>

          {expanded && canDownload && <EventTimeline docId={doc.id} />}
        </div>
      </div>
    </div>
  )
}

function RecentUploads() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ingestion-recent'],
    queryFn: () => getRecentIngestion(50),
    refetchInterval: (query) => {
      const docs: DocumentRow[] = query.state.data?.documents ?? []
      if (docs.length === 0) return 2000
      const allTerminal = docs.every((d) => TERMINAL_STATUSES.has(d.status))
      return allTerminal ? false : 2000
    },
  })

  if (isLoading)
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading recent uploads…
      </div>
    )

  if (isError)
    return <div className="py-8 text-center text-sm text-destructive">Could not load recent uploads.</div>

  const docs: DocumentRow[] = data?.documents ?? []

  if (docs.length === 0)
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No uploads yet — drop files above to get started.
      </div>
    )

  return (
    <div className="divide-y divide-border">
      {docs.map((doc) => (
        <DocumentItem key={doc.id} doc={doc} />
      ))}
    </div>
  )
}

// ─── Autonomous intake ─────────────────────────────────────────────────────────
// The two reflexes that grow the system: forward a candidate (email+CV → pool),
// and paste a JD (→ a structured role, honouring your JD-intake automation mode).
function IntakePanel() {
  const [emailBody, setEmailBody] = useState('')
  const [cvFile, setCvFile] = useState<File | null>(null)
  const [jd, setJd] = useState('')
  const [busy, setBusy] = useState<'email' | 'jd' | null>(null)
  const cvRef = useRef<HTMLInputElement>(null)
  const cls = 'w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring'

  const sendEmail = async () => {
    if (!emailBody.trim() && !cvFile) { toast.error('Paste an email and/or attach a CV.'); return }
    setBusy('email')
    try {
      const r = await ingestEmail(emailBody, cvFile ?? undefined)
      toast.success(r.isNew ? `Added ${r.name ?? 'candidate'} to the pool` : `Merged into existing candidate ${r.name ?? ''}`.trim())
      setEmailBody(''); setCvFile(null)
    } catch { toast.error('Could not ingest that candidate.') } finally { setBusy(null) }
  }
  const sendJd = async () => {
    if (!jd.trim()) { toast.error('Paste a JD.'); return }
    setBusy('jd')
    try {
      const r = await ingestJd(jd)
      toast.success(r.mode === 'auto' ? 'Role created from JD' : r.mode === 'off' ? 'JD intake is set to Off in Automation' : 'Role drafted — approve it in Automation')
      setJd('')
    } catch { toast.error('Could not parse that JD.') } finally { setBusy(null) }
  }

  return (
    <div className="mb-6 grid gap-4 md:grid-cols-2">
      <Card>
        <CardContent className="pt-4 pb-4">
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground"><Mail className="h-4 w-4 text-primary" /> Forward a candidate</h3>
          <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={3} placeholder="Paste the email — candidate details + your notes…" className={cls} />
          <div className="mt-2 flex items-center justify-between gap-2">
            <button type="button" onClick={() => cvRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/50">
              <Paperclip className="h-3.5 w-3.5" /> {cvFile ? cvFile.name : 'Attach CV'}
            </button>
            <input ref={cvRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden" onChange={(e) => setCvFile(e.target.files?.[0] ?? null)} />
            <Button size="sm" disabled={busy === 'email'} onClick={sendEmail}>{busy === 'email' ? 'Adding…' : 'Add to pool'}</Button>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">Parsed, redacted, deduped (by phone/email) and added — combining the email + CV.</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-4">
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground"><Sparkles className="h-4 w-4 text-primary" /> Paste a JD → role</h3>
          <textarea value={jd} onChange={(e) => setJd(e.target.value)} rows={3} placeholder="Paste a job description…" className={cls} />
          <div className="mt-2 flex items-center justify-end">
            <Button size="sm" disabled={busy === 'jd'} onClick={sendJd}>{busy === 'jd' ? 'Creating…' : 'Create role'}</Button>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">AI structures the JD into a role. Honours your <span className="font-medium">JD-intake</span> automation mode (Auto / Review / Off).</p>
        </CardContent>
      </Card>
    </div>
  )
}
